import React, { useMemo } from 'react';
import classNames from 'classnames';
import { DATA_TYPE_OBJECT } from '@home/utils/common';
import { platforms } from '@/pages/dashboard/pages/overview/_config/platforms';
import MetricsCardTrendChart from '@/pages/dashboard/components/MetricsCardTrendChart';
import { getJumpWithParams, jumpDetail, openWithParams } from '@/pages/dashboard/utils/utils';
import useStudioExplain from '@home/hooks/useStudioExplain';
import { LIGHT_COLOR_ARR } from '@customComp/Chart/_config/_constant';
import { DEFAULT_PLATFORM_DISABLE } from '@home/pages/studio/config';
import { mapObjectValue } from '@home/pages/studio/utils';
import MetricsCardWrap from '@/pages/dashboard/components/MetricsCardWrap';
import MetricsCardValue from '@/pages/dashboard/components/MetricsCardValue';
import MetricsCardDod from '@/pages/dashboard/components/MetricsCardDod';
import MetricsCardRank from '@/pages/dashboard/components/MetricsCardRank';
import './index.scss';
import moment from 'moment';
import { extractParamsFromCarryFilter, getFilterConfig } from '../utils';
import LoadingFailed from '@/pages/dashboard/components/LoadingFailed';
import { JumpType, navigate } from '@home/utils/utils';

const StudioMetricCardTrendAndRank = (props: any) => {
  const {
    config,
    filter,
    className,
    height = 75,
    disablePlatform,
    style,
    gameConfig,
    rankType = "os",
    apiDataKey,
    errorStyle,
    filterConfig
  } = props || {};
  const {
    titleEn,
    titleCn,
    titleEnChange,
    titleCnChange,
    imgUrl,
    dodData,
    formatRule,
    titleFormatRule,
    metricKey,
    valueDisplayMode,
    rainbowKey,
    isRightCard,
    isLeftCard,
    explainEn,
    explainCn,
    trendConfig,
    link,
    jumpParams = {},
    sliceLength = 4,
    jumpType = '',
    extraCarryParams={},
    preUnit,
    endUnit,
    cardType,
  } = config || {};
  const { date_type: dataType, isSingle, start_time: startTime, end_time: endTime, payType, selectorList} = filter || {};
  const carryFilter = getFilterConfig(selectorList, filterConfig);
  const { data, errorMsg } = props?.data?.[apiDataKey] || {};
  const {
    breakdown: rankList,
    card_list: cardList,
    trend_list: trendList,
  } = data || {};
  const disablePlatformList = (disablePlatform || DEFAULT_PLATFORM_DISABLE).map(
    (name: string) => ({
      full_name: name,
      value: '',
      share_of_all: '',
    }),
  );

  // 筛选器继承参数
  const filterCarry = useMemo(() => {
    let baseParams;
  
    if (cardType === 'opinion') {
      baseParams = {
        start_time: startTime,
        end_time: endTime,
        date_type: dataType,
      };
    } else {
      baseParams = {
        start: startTime,
        end: endTime,
        timeType: DATA_TYPE_OBJECT[dataType],
        ...(payType ? { payment_type: payType } : {}),
      };
    }
  
    // 合并额外参数
    return { ...baseParams, ...extractParamsFromCarryFilter(extraCarryParams,carryFilter) };
  }, [filter, cardType, extraCarryParams, carryFilter]);

  // 获取七彩石指标注释
  const { tipEn, tipCn } = useStudioExplain(rainbowKey, explainEn, explainCn);
  // 根据配置组装对应DodData
  const selectedDataField = DATA_TYPE_OBJECT[dataType] || 'Day';
  const cardData = cardList?.find(
    (card: { metrics_name: string; }) => card?.metrics_name === metricKey,
  );

  const dodValues = Object.entries(dodData?.[selectedDataField] ?? {})
    ?.map(([key, dodItem]: [string, any]) => {
      if (!isSingle && key !== 'change') {
        return null;
      }
      if (isSingle && key === 'change') {
        return null;
      }
      if (key === 'dod' || key === 'sdlw' || key === 'change') {
        const dataToUse = cardData?.[key];
        return {
          titleEn: dodItem?.titleEn,
          titleCn: dodItem?.titleCn,
          formatRule: dodItem?.formatRule,
          extendFormatRule: dodItem?.extendFormatRule,
          thisTime: dataToUse?.this_time || '',
          lastTime: dataToUse?.last_time || '',
          value: dataToUse?.show_value || '',
          thisValue: dataToUse?.this_value || '',
          lastValue: dataToUse?.last_value || '',
        };
      }
      return null; // 如果 key 不符合条件，返回 null
    })
    .filter(Boolean);

  // 合并 PlayStation 数据并创建 rankData , 按照value降序再截取
  const rankData = (rankList && rankList.length > 0 ? rankList : disablePlatformList)
    .filter(
      (item: { name: string; }) => item?.name?.trim().toLowerCase() !== 'others',
    ) // Filter out 'others'
    .reduce((acc, item, index) => {
      const platformName = item?.full_name.trim().toLowerCase();
      // 定义合并后的平台名称
      const mergedPlatformName = platformName.includes('playstation')
        ? 'PlayStation'
        : item?.full_name;
      // 查找已存在的平台
      const existingPlatform = acc.find(
        (p: { nameEn: string; }) => p?.nameEn === mergedPlatformName,
      );
      if (existingPlatform) {
        // 如果已存在，则累加数值
        existingPlatform.value += parseFloat(item?.value) || '';
        existingPlatform.subValue += parseFloat(item?.share_of_all) || '';
      } else {
        // 如果不存在，则创建新对象
        const platform = platforms.find((platformItem) =>
          mergedPlatformName
            ?.toLowerCase()
            .includes(platformItem?.title.toLowerCase()),
        );

        acc.push({
          nameEn: mergedPlatformName,
          value: parseFloat(item?.value) || '',
          subValue: parseFloat(item?.share_of_all) || '',
          icon: platform?.icon,
          disableIcon: platform?.disableIcon,
          color: platform?.color || LIGHT_COLOR_ARR[index],
          valueDisplayMode,
          isBlackImg: platform?.isBlack,
        });
      }

      return acc;
    }, [])
    ?.sort((a: { value: number; }, b: { value: number; }) => b?.value - a?.value)
    ?.slice(0, sliceLength);

  // 游戏占比数据
  const gameRankData = rankList?.map((item: { name: string; value: string; share_of_all: string; },index: number) => {
    const game = gameConfig?.find((game: { gameCode: string; }) => game.gameCode === item.name) || {};
      
    return {
      nameEn: game?.nameEn || '',
      nameCn: game?.nameCn || '',
      icon: game?.img || '',
      value: parseFloat(item?.value) || '',
      subValue: parseFloat(item?.share_of_all) || '',
      color: game?.color || LIGHT_COLOR_ARR[index],
      link: game?.link ? () => onClick(game?.link) : null,
      windowLink: getJumpWithParams(game?.link, filterCarry)
    };
  });

  // 根据 isSingle 动态修改 titleEn 和 titleCn
  const titleEN = isSingle ? titleEn : titleEnChange || titleEn;
  const titleCN = isSingle ? titleCn : titleCnChange || titleCn;

  // 卡片下钻
  const onClick = (customLink?: string) => {
    const realLink = customLink || link;
    // 筛选器继承跳转
    if (realLink) {
      if (
        jumpParams?.filterValue &&
        isSingle &&
        jumpParams?.isComplete &&
        filter?.jumpTime
      ) {
        jumpParams.filterValue.rangeTime = filter?.jumpTime;
      }

      navigate(realLink, { jumpType: JumpType.Window, windowParams: filterCarry });

      // 旧逻辑，下钻详情页（目前默认打开新窗口，不再需要下钻）
      // if (jumpParams?.filterValue) {
      //   jumpParams.filterValue = {
      //     ...mapObjectValue(jumpParams.filterValue,filter,),
      //     ...extractParamsFromCarryFilter(extraCarryParams,carryFilter)
      //   };
      //   // 将时间范围加到筛选器下转参数
      //   jumpParams.filterValue.rangeTime = [
      //     moment(filter.start_time).format('YYYY-MM-DD'),
      //     moment(filter.end_time).format('YYYY-MM-DD'),
      //   ];
      // }
      // jumpDetail(realLink, jumpParams);
    }
  };

  if (errorMsg) {
    return <div style={{
      height: errorStyle?.height,
      color: '#444',
      background: '#fff',
      borderRadius: 8,
    }}><LoadingFailed /></div>;
  }

  return (
    <div  className={classNames('dbh-studio-home-trend-rank-card',className)}>
      <MetricsCardWrap
        titleCn={titleCn}
        titleEn={titleEn}
        className={classNames( {'no-link': !link ,})}
        style={style}
        link={link ? onClick : null}
        tipsEn={tipEn}
        tipsCn={tipCn}
        imgUrl={imgUrl}
      >
        <div className={`dbd-metrics-card-with-rank__flex dbd-metrics-card-with-rank__row`}>
          <div className={`dbh-studio-home-trend-rank-card-left`}>
            <div className={'dbh-studio-home-trend-rank-card-value'}>
              <MetricsCardValue 
                className={''}
                style={{}}
                value={cardData?.value}
                formatRule={titleFormatRule ? titleFormatRule : formatRule}
                emptyValue={"-"}
                preUnit={preUnit}
                endUnit={endUnit}
              />
              <MetricsCardDod 
                className={""}
                style={{}}
                data={dodValues}
              />
            </div>
            <div className={`dbh-studio-home-trend-rank-card-trend`}>
              <MetricsCardTrendChart
                height={height}
                titleEn={titleEN}
                titleCn={titleCN}
                isRightCard={isRightCard}
                isLeftCard = {isLeftCard}
                metricData={trendList}
                loading={false}
                error={false}
                filterVal={{ timeType: selectedDataField }}
                componentMetrics={[trendConfig?.[selectedDataField] || {}]}
              />
            </div>
          </div>
          <div className={`dbh-studio-home-trend-rank-card-right`}>
            <div className={`dbh-studio-home-trend-rank-card-rank`}>
              <MetricsCardRank 
                className={classNames({
                  'dbh-studio-home-trend-rank-card-rank-game': rankType === "game",
                })}
                style={{}}
                data={rankType === "game"? gameRankData : rankData} 
                formatRule={titleFormatRule ? titleFormatRule : formatRule}
                // subFormatRule={subFormatRule}
              />
            </div>
          </div>
        </div>
      </MetricsCardWrap>
    </div>
  );
};

export default StudioMetricCardTrendAndRank;
